import React from 'react';
import { PieChart, Pie, ResponsiveContainer, Cell, Tooltip } from 'recharts';
import { palette } from '@shipium-corp/shipium-ui-theme';
import type { ColorPaletteRecord } from '@shipium-corp/shipium-ui-theme';
export interface CompactPieDatum {
  id: string;
  label: string;
  key: string;
  value: number;
  percent: number;
  color?: ColorPaletteRecord;
}
export interface CompactPieChartProps {
  data: CompactPieDatum[];
  showLabel?: boolean;
  color?: ColorPaletteRecord;
  customToolTip?: any;
}

const hexToRGB = (hex: string, alpha: string) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  alpha = hex.length > 7 ? String(parseInt(hex.slice(7, 9), 16) / 255) : alpha;
  if (alpha) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } else {
    return `rgb(${r}, ${g}, ${b})`;
  }
};

const getColorForPercent = (percent: number, color: ColorPaletteRecord) => {
  if (percent < 0.2) {
    return hexToRGB(color.light, '1');
  } else {
    return hexToRGB(color.normal, '1');
  }
};

export const DonutChartCompact = ({ data = [], showLabel = true, customToolTip, color }: CompactPieChartProps) => {
  let otherValue = 0;
  const cleanData = data.filter((datum) => datum.value > 0);
  const totalValue = cleanData.reduce((sum, curr) => sum + curr.value, 0);
  const filteredData = cleanData.filter((datum) => {
    if (datum.value < totalValue * 0.01) {
      otherValue += datum.value;
      return false;
    }
    return true;
  });

  if (totalValue < 100) {
    filteredData.push({
      id: 'Other',
      label: 'Other',
      key: 'Other',
      value: otherValue,
      percent: otherValue / totalValue >= 0.01 ? otherValue / totalValue : 0.01,
    });
  }

  return filteredData.length > 0 ? (
    <ResponsiveContainer minHeight={300} minWidth={500}>
      <PieChart>
        {customToolTip ? <Tooltip content={customToolTip} /> : <Tooltip />}
        <Pie
          data={filteredData}
          minAngle={5}
          paddingAngle={1}
          isAnimationActive={false} // required to make labels work // https://github.com/recharts/recharts/issues/1135
          label={
            showLabel
              ? (entry: any) => {
                  const { innerRadius, outerRadius, midAngle, cx, cy } = entry;
                  const RADIAN = Math.PI / 180;
                  // eslint-disable-next-line
                  const radius = 25 + innerRadius + (outerRadius - innerRadius);
                  // eslint-disable-next-line
                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                  // eslint-disable-next-line
                  const y = cy + radius * Math.sin(-midAngle * RADIAN);

                  return 100 * entry.percent >= 1 ? (
                    <text
                      style={{ fontFamily: 'monospace', fontSize: '10px' }}
                      x={x}
                      y={y}
                      textAnchor={x > cx ? 'start' : 'end'}
                      dominantBaseline={'central'}
                    >
                      {entry.label} {(100 * entry.percent).toFixed(0)}%
                    </text>
                  ) : null;
                }
              : undefined
          }
          labelLine={true}
          dataKey="value"
          nameKey="label"
          innerRadius={50}
          outerRadius={100}
        >
          {data.map((entry, index) => {
            return (
              <Cell key={entry.id} fill={getColorForPercent(entry.percent, entry.color || color || palette.Teal)} />
            );
          })}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  ) : null;
};
